/**
 * Putting a creator's own picture, clip or sound where a published app can reach it.
 *
 * Two steps, both the server's design: ask for a presigned slot, then PUT the bytes straight
 * to storage. The bytes never pass through the API, which is what lets a video be larger than
 * a Lambda request body.
 *
 * The size is DECLARED and the server signs the URL with it, so a caller who says 100KB
 * cannot then send 5GB. A `File` knows its own byte length, so this sends the real number
 * rather than an estimate — a wrong one is refused by S3 at PUT time, after the round trip.
 */
import { ApiError, request } from './client';

export interface UploadSlot {
  uploadUrl: string;
  fileUrl: string;
  assetId?: string;
}

export interface UploadedAsset {
  /** What the generated app calls it: `S.media('cover.jpg')`. */
  id: string;
  /** Where the page will actually fetch it from. */
  url: string;
}

/** A picture the creator attached, before anything has been uploaded. */
export interface AttachedFile {
  /** The NAME is the app's handle on it — the model was given this, not a URL. */
  name: string;
  contentType: string;
  file: File;
}

/**
 * Upload one file against a published app.
 *
 * Throws rather than returning null: the caller decides whether a missing picture should
 * stop a publish, and it should not — but it must be able to say so.
 */
export async function uploadMedia(appId: string, item: AttachedFile): Promise<UploadedAsset> {
  const slot = await request<UploadSlot>(`/apps/${encodeURIComponent(appId)}/upload`, {
    method: 'POST',
    body: {
      fileName: item.name,
      contentType: item.contentType,
      // Bytes, from the file itself. The server refuses a missing or zero size.
      size: item.file.size,
      category: 'general',
    },
  });
  if (!slot.uploadUrl || !slot.fileUrl) {
    throw new ApiError('server', 'Storage did not offer a place to put that file.');
  }

  const res = await fetch(slot.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': item.contentType },
    body: item.file,
  });
  if (!res.ok) throw new ApiError('server', `That file did not upload (${res.status}).`);

  return { id: item.name, url: slot.fileUrl };
}

/**
 * Upload everything attached, and answer with what actually arrived.
 *
 * ONE AT A TIME, deliberately. Parallel uploads of a handful of photos are a burst against an
 * account that can run very few functions at once, and a partial burst is worse than a slow
 * success: the app was written against these names, and a file that fails is simply absent —
 * `S.media` answers null for it rather than yielding a dead address.
 */
export async function uploadAllMedia(
  appId: string,
  items: AttachedFile[],
): Promise<{ uploaded: UploadedAsset[]; failed: string[] }> {
  const uploaded: UploadedAsset[] = [];
  const failed: string[] = [];
  for (const item of items) {
    try {
      uploaded.push(await uploadMedia(appId, item));
    } catch {
      failed.push(item.name);
    }
  }
  return { uploaded, failed };
}
